import React, { useState, useEffect } from "react";

const PlatformConfigCard = ({
  platform,
  icon,
  iconColor,
  config,
  onUpdate,
  loading,
}) => {
  const [formData, setFormData] = useState({
    minVersion: "",
    latestVersion: "",
    force: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (config) {
      setFormData({
        minVersion: config.minVersion || "",
        latestVersion: config.latestVersion || "",
        force: config.force || false,
      });
    }
  }, [config]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateVersion = (version) => {
    const versionRegex = /^\d+\.\d+\.\d+$/;
    return versionRegex.test(version);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.minVersion.trim()) {
      newErrors.minVersion = "Minimum version is required";
    } else if (!validateVersion(formData.minVersion)) {
      newErrors.minVersion = "Version must be in format x.y.z (e.g., 1.0.0)";
    }

    if (!formData.latestVersion.trim()) {
      newErrors.latestVersion = "Latest version is required";
    } else if (!validateVersion(formData.latestVersion)) {
      newErrors.latestVersion = "Version must be in format x.y.z (e.g., 1.0.0)";
    }

    // Check if latest version is greater than or equal to min version
    if (
      formData.minVersion &&
      formData.latestVersion &&
      validateVersion(formData.minVersion) &&
      validateVersion(formData.latestVersion)
    ) {
      const minParts = formData.minVersion.split(".").map(Number);
      const latestParts = formData.latestVersion.split(".").map(Number);

      for (let i = 0; i < 3; i++) {
        if (latestParts[i] > minParts[i]) break;
        if (latestParts[i] < minParts[i]) {
          newErrors.latestVersion =
            "Latest version must be greater than or equal to minimum version";
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onUpdate(formData);
    }
  };

  const isFormChanged = () => {
    return (
      formData.minVersion !== (config?.minVersion || "") ||
      formData.latestVersion !== (config?.latestVersion || "") ||
      formData.force !== (config?.force || false)
    );
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-light">
        <div className="d-flex align-items-center">
          <div className={`me-3 text-${iconColor}`}>
            <i className={`${icon} fs-4`}></i>
          </div>
          <div>
            <h6 className="fw-bold mb-0">{platform} Configuration</h6>
            <small className="text-muted">
              Manage {platform} app version settings
            </small>
          </div>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Minimum Version */}
          <div className="mb-3">
            <label className="form-label fw-medium">
              Minimum Version <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${
                errors.minVersion ? "is-invalid" : ""
              }`}
              name="minVersion"
              value={formData.minVersion}
              onChange={handleInputChange}
              placeholder="e.g., 1.0.0"
              disabled={loading}
            />
            {errors.minVersion && (
              <div className="invalid-feedback">{errors.minVersion}</div>
            )}
            <small className="form-text text-muted">
              Users with versions below this will be prompted to update
            </small>
          </div>

          {/* Latest Version */}
          <div className="mb-3">
            <label className="form-label fw-medium">
              Latest Version <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${
                errors.latestVersion ? "is-invalid" : ""
              }`}
              name="latestVersion"
              value={formData.latestVersion}
              onChange={handleInputChange}
              placeholder="e.g., 1.2.0"
              disabled={loading}
            />
            {errors.latestVersion && (
              <div className="invalid-feedback">{errors.latestVersion}</div>
            )}
            <small className="form-text text-muted">
              The current latest version available in the store
            </small>
          </div>

          {/* Force Update */}
          <div className="mb-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id={`force-${platform.toLowerCase()}`}
                name="force"
                checked={formData.force}
                onChange={handleInputChange}
                disabled={loading}
              />
              <label
                className="form-check-label fw-medium"
                htmlFor={`force-${platform.toLowerCase()}`}
              >
                Force Update Required
              </label>
            </div>
            <small className="form-text text-muted">
              When enabled, users with versions below minimum will be forced to
              update
            </small>
          </div>

          {/* Current Status */}
          <div className="mb-3 p-3 bg-light rounded">
            <h6 className="fw-bold mb-2">Current Status</h6>
            <div className="row">
              <div className="col-6">
                <small className="text-muted">Min Version:</small>
                <div className="fw-bold">{config?.minVersion || "Not set"}</div>
              </div>
              <div className="col-6">
                <small className="text-muted">Latest Version:</small>
                <div className="fw-bold">
                  {config?.latestVersion || "Not set"}
                </div>
              </div>
            </div>
            <div className="mt-2">
              <span
                className={`badge ${
                  config?.force ? "bg-danger" : "bg-success"
                }`}
              >
                {config?.force ? "Force Update: ON" : "Force Update: OFF"}
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="d-grid">
            <button
              type="submit"
              className={`btn btn-${iconColor}`}
              disabled={loading || !isFormChanged()}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Update {platform} Configuration
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformConfigCard;
